import {differenceInDays, parseISO, startOfToday, format, isToday, isTomorrow, isFuture, isValid} from 'date-fns';
import type {
	Gsoa_Coupons_GetCustomerCoupon_response as Coupon,
	CustomerCoupon,
} from '~/openapi';
import {CouponType, GiftType, type GiftRuntime, type GiftWithType} from '~/types/coupon';

export type GiftBucket = 'active' | 'pending' | 'used';

export function getCouponType(coupon: Coupon): CouponType | null {
	if (![20, 21, 22, 23, 24, 25, 26, 3, 4, 10].includes(coupon.CategoryId)) {
		return null;
	}

	const isActivable = [20, 21, 22, 23, 24, 25, 26].includes(coupon.CategoryId);

	switch (true) {
		case isFuture(new Date(coupon.ValidFrom)) && !coupon.Activated:
			return CouponType.PENDING;
		case coupon.Used === 2:
			return CouponType.USED;
		case !coupon.IsPermanent && coupon.Activated && [3, 4, 10].includes(coupon.CategoryId):
			return CouponType.ONE_TIME;
		case isActivable && !coupon.Activated:
			return CouponType.CAN_BE_ACTIVATED;
		case isActivable && coupon.Activated:
			return CouponType.IS_ACTIVATED;
		case coupon.CategoryId === 28:
			return CouponType.SPECIFIC;
		default:
			return null;
	}
}

export function getCouponExpirationString(coupon: Coupon): string {
	const couponValidFrom = parseISO(coupon.ValidFrom);
	const couponValidTo = parseISO(coupon.ValidTo);
	const daysUntilValidFrom = differenceInDays(couponValidFrom, startOfToday());
	const daysUntilExpiration = differenceInDays(couponValidTo, startOfToday());

	if (coupon.Used === 2) { return ''; }

	switch (true) {
		case daysUntilValidFrom > 0:
			switch (true) {
				case daysUntilValidFrom === 1:
					return 'Kupon bude platný od zítřka';
				case daysUntilValidFrom >= 2 && daysUntilValidFrom <= 4:
					return `Kupon bude platný za ${daysUntilValidFrom} dny`;
				case daysUntilValidFrom >= 5:
					return `Kupon bude platný za ${daysUntilValidFrom} dní`;
				default:
					return `Kupon bude platný od ${format(couponValidFrom, 'd. M. yyyy')}`;
			}
		case isToday(couponValidTo):
			return 'Kupon vyprší dnes';
		case isTomorrow(couponValidTo):
			return 'Kupon vyprší zítra';
		case daysUntilExpiration >= 2 && daysUntilExpiration <= 4:
			return `Kupon vyprší za ${daysUntilExpiration} dny`;
		case daysUntilExpiration <= 0:
			return `Kupon vypršel ${format(couponValidTo, 'd. M. yyyy')}`;
		case daysUntilExpiration >= 5:
			return `Kupon vyprší za ${daysUntilExpiration} dní`;
		default:
			return '';
	}
}

export function getGiftExpirationString(gift: {ValidTo: string}): string {
	const couponValidTo = parseISO(gift.ValidTo);
	const daysUntilExpiration = differenceInDays(couponValidTo, startOfToday());
	switch (true) {
		case isToday(couponValidTo):
			return 'Odměna vyprší dnes';
		case isTomorrow(couponValidTo):
			return 'Odměna vyprší zítra';
		case daysUntilExpiration >= 2 && daysUntilExpiration <= 4:
			return `Odměna vyprší za ${daysUntilExpiration} dny`;
		case daysUntilExpiration >= 5:
			return `Odměna vyprší za ${daysUntilExpiration} dní`;
		default:
			return '';
	}
}

export function getGiftDescription(gift: GiftWithType | undefined, isRepeatable: boolean, isOneTime: boolean): string | null {
	if (!gift) {
		return null;
	}

	const singleUse = gift?.coupon.SingleUse?.trim();

	if ([GiftType.ACTIVE, GiftType.PRE_ACTIVE, GiftType.USED, GiftType.INFORMATIONAL].includes(gift.type) && singleUse) {
		return singleUse;
	} else if ((gift.type === GiftType.ACTIVE || gift.type === GiftType.PRE_ACTIVE || gift.type === GiftType.INFORMATIONAL) && isRepeatable) {
		return 'Tuto odměnu lze uplatnit opakovaně.';
	} else if ((gift.type === GiftType.ACTIVE || gift.type === GiftType.PRE_ACTIVE || gift.type === GiftType.USED) && isOneTime) {
		return 'Tuto odměnu lze uplatnit pouze jednou.';
	} else if (gift.type === GiftType.INFORMATIONAL) {
		return String(gift.coupon.SingleUse);
	}
	return null;
}

export function normalizeActivated(value: unknown): boolean {
	if (typeof value === 'boolean') {
		return value;
	}

	if (typeof value === 'number') {
		return value === 1;
	}

	if (typeof value === 'string') {
		const normalizedValue = value.trim().toLowerCase();
		return normalizedValue === '1' || normalizedValue === 'true';
	}

	return false;
}

export function getGiftType(gift: GiftRuntime, bucket: GiftBucket): GiftType {
	if (bucket === 'pending') {
		return GiftType.PENDING;
	}

	if (bucket === 'used') {
		return GiftType.USED;
	}

	return normalizeActivated(gift.coupon.Activated) ? GiftType.ACTIVE : GiftType.PRE_ACTIVE;
}

export function getGiftUsedDate(gift: GiftRuntime): string | null {
	return gift.usedDateTime ?? gift.UsedDateTime ?? gift.coupon.UsedDateTime ?? null;
}

export function getGiftUsedText(gift: GiftRuntime): string {
	const usedDate = getGiftUsedDate(gift);

	if (!usedDate) {
		return 'Uplatněna';
	}

	const parsedDate = parseISO(usedDate);

	if (!isValid(parsedDate)) {
		return 'Uplatněna';
	}

	return `Uplatněna ${format(parsedDate, 'd. M.')}`;
}

export function getGiftProgress(accountPoints: unknown, pointsLimit: unknown): number {
	const accountPointsValue = Number(accountPoints);
	const pointsLimitValue = Number(pointsLimit);

	if (!Number.isFinite(accountPointsValue) || !Number.isFinite(pointsLimitValue) || pointsLimitValue <= 0) {
		return 0;
	}

	return Math.min(Math.max(Number((accountPointsValue / pointsLimitValue).toFixed(2)), 0), 1);
}

export function getPointsToGift(accountPoints: unknown, pointsLimit: unknown): number {
	const accountPointsValue = Number(accountPoints);
	const pointsLimitValue = Number(pointsLimit);

	if (!Number.isFinite(accountPointsValue) || !Number.isFinite(pointsLimitValue) || pointsLimitValue <= 0) {
		return 0;
	}

	return Math.max(pointsLimitValue - accountPointsValue, 0);
}

function getGiftValidToTime(gift: Pick<GiftRuntime, 'coupon'>): number {
	const validToTime = new Date(gift.coupon.ValidTo).getTime();
	return Number.isFinite(validToTime) ? validToTime : Number.MAX_SAFE_INTEGER;
}

export function giftValidToComparator(a: Pick<GiftRuntime, 'coupon'>, b: Pick<GiftRuntime, 'coupon'>) {
	return getGiftValidToTime(a) - getGiftValidToTime(b);
}

export function mapGiftsWithType<GT extends GiftType>(gifts: CustomerCoupon[] | undefined, type: GT): GiftWithType<GT>[] {
	return gifts?.filter((item) => isFuture(new Date(item.coupon.ValidTo))).map((gift) => ({...gift, type})) ?? [];
}

export function mapGiftsByBucket(gifts: GiftRuntime[] | undefined, bucket: GiftBucket): GiftWithType[] {
	return (bucket === 'used' ? gifts ?? [] : gifts?.filter((item) => isFuture(new Date(item.coupon.ValidTo))) ?? [])
		.map((gift) => {
			return {
				...gift,
				type: getGiftType(gift, bucket),
			};
		}) ?? [];
}

export function canUseCouponInHypermarket(coupon: Coupon, hypermarketGsoaId: number | null): boolean {
	const isLocalAssignment = coupon.Assignment === 'local' || coupon.Assignment === 'local-personal';
	const matchesSelectedHypermarket = coupon.HouseNumbers?.includes(Number(hypermarketGsoaId)) ?? false;

	if (isLocalAssignment) {
		return matchesSelectedHypermarket;
	}

	return true;
}
